import { History } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const HISTO_LIGNES = [
    ["ca", "CA réalisé"], ["charges_exploitation", "Charges d'exploitation"], ["masse_salariale", "Masse salariale"],
    ["charges_fixes", "Charges fixes"], ["dettes_fournisseurs", "Dettes fournisseurs"],
    ["dettes_fiscales_sociales", "Dettes fiscales et sociales"], ["creances_clients", "Créances clients"],
];
const N = 6;
const emptyRows = () => Object.fromEntries(HISTO_LIGNES.map(([k]) => [k, Array(N).fill("")]));

export function normHistorique(h) {
    const annee = Number(h?.annee_courante) || new Date().getFullYear();
    const valeurs = emptyRows();
    for (const [k] of HISTO_LIGNES) {
        const row = Array.isArray(h?.valeurs?.[k]) ? h.valeurs[k] : [];
        valeurs[k] = Array.from({ length: N }, (_, i) => (row[i] ?? "") === null ? "" : String(row[i] ?? ""));
    }
    return { annee_courante: annee, valeurs, commentaire: h?.commentaire || "" };
}

export const hasHistorique = (h) => !!h && Object.values(h.valeurs || {}).some((row) => row.some((v) => String(v || "").trim()));

/** Grille N..N-5 : repli déclaratif quand le client n'a pas de FEC / liasses (restructuration uniquement). */
export default function HistoriqueManuelGrid({ value, onChange, disabled = false, prefix = "histo" }) {
    const v = normHistorique(value);
    const setCell = (k, i, val) => onChange({ ...v, valeurs: { ...v.valeurs, [k]: v.valeurs[k].map((x, j) => (j === i ? val : x)) } });
    const cols = Array.from({ length: N }, (_, i) => (i === 0 ? `Année en cours (${v.annee_courante})` : `N-${i} (${v.annee_courante - i})`));
    return (
        <section data-testid={`${prefix}-card`} className="rounded-xl border border-amber-200 bg-white p-5 shadow-sm space-y-4">
            <header className="flex flex-wrap items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600"><History className="h-4 w-4" strokeWidth={1.75} /></span>
                <div className="flex-1 min-w-[200px]">
                    <h3 className="text-base font-semibold text-slate-900">Historique Financier Manuel (À remplir en l&apos;absence de documents comptables)</h3>
                    <p className="text-xs text-slate-500">Montants annuels en euros. Si vous disposez des FEC ou liasses, déposez-les plutôt dans l&apos;espace documents.</p>
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-600">Année en cours
                    <Input data-testid={`${prefix}-annee`} inputMode="numeric" value={v.annee_courante} disabled={disabled} onChange={(e) => onChange({ ...v, annee_courante: Number(e.target.value) || v.annee_courante })} className="h-8 w-20 text-center" />
                </label>
            </header>
            <div className="relative overflow-x-auto">
                <table data-testid={`${prefix}-table`} className="w-full text-sm">
                    <thead><tr><th className="text-left py-2 pr-3 min-w-[200px]">Poste</th>{cols.map((c) => <th key={c} className="text-right py-2 px-1 min-w-[110px]">{c}</th>)}</tr></thead>
                    <tbody>
                        {HISTO_LIGNES.map(([k, label]) => (
                            <tr key={k} className="even:bg-slate-50">
                                <td className="py-1.5 pr-3 text-slate-700">{label}</td>
                                {v.valeurs[k].map((cell, i) => (
                                    <td key={`${k}-${i}`} className="py-1 px-1">
                                        <Input data-testid={`${prefix}-${k}-${i}`} inputMode="decimal" value={cell} disabled={disabled} placeholder="—" onChange={(e) => setCell(k, i, e.target.value)} className="h-8 text-right border-slate-300" />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Textarea data-testid={`${prefix}-commentaire`} rows={3} value={v.commentaire} disabled={disabled} onChange={(e) => onChange({ ...v, commentaire: e.target.value })} placeholder="Précisions (exercice décalé, chiffres estimés, événements marquants…)" className="bg-white text-sm min-h-[80px]" />
        </section>
    );
}
